/**
 * Hand-written route tree (code-based router). We use TanStack Router's code-based
 * routes rather than file-based so plugin pages can be registered dynamically at
 * runtime via the catch-all `pluginRoute`.
 */
import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, Outlet, useNavigate, useParams } from '@tanstack/react-router';
import { Shell } from './components/shell/Shell.js';
import { DashboardPage } from './pages/Dashboard.js';
import { LoginPage } from './pages/Login.js';
import { InvitePage } from './pages/Invite.js';
import { OrgsPage } from './pages/Orgs.js';
import { OrgDetailPage } from './pages/OrgDetail.js';
import { AccountPage } from './pages/Account.js';
import { ApiKeysPage } from './pages/ApiKeys.js';
import { StyleGuidePage } from './pages/StyleGuide.js';
import { PluginPage } from './pages/Plugin.js';
import { ApplicationsPage } from './pages/applications/Applications.js';
import { ApplicationDetailPage } from './pages/applications/ApplicationDetail.js';
import { ServersPage } from './pages/applications/Servers.js';
import { ServerDetailPage } from './pages/applications/ServerDetail.js';
import { ScriptDetailPage } from './pages/applications/ScriptDetail.js';
import { AccessMethodsPage } from './pages/applications/AccessMethods.js';
import { AccessMethodDetailPage } from './pages/applications/AccessMethodDetail.js';
import { SecretsPage } from './pages/applications/Secrets.js';
import { SecretDetailPage } from './pages/applications/SecretDetail.js';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InvitePage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: Shell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: DashboardPage,
});

const orgsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/orgs',
  component: OrgsPage,
});

const orgDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/orgs/$slug',
  component: OrgDetailPage,
});

const apiKeysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/api-keys',
  component: ApiKeysPage,
});

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account',
  component: AccountPage,
});

const styleGuideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/style-guide',
  component: StyleGuidePage,
});

/** Applications plugin — specific routes. The catch-all still handles any
 *  other plugin id, but the applications plugin is owned by code here. The
 *  plugin was previously called "hosting"; legacy /p/hosting/* paths get a
 *  client-side redirect at the bottom of this file. */
const applicationsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/applications',
  component: ApplicationsPage,
});

const applicationDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/applications/applications/$slug',
  component: ApplicationDetailPage,
  // Allow ?org=<slug> as a disambiguator when an app slug exists in multiple orgs.
  validateSearch: (raw): { org?: string } => {
    const o = raw['org'];
    return typeof o === 'string' ? { org: o } : {};
  },
});

const serversIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/applications/servers',
  component: ServersPage,
});

const serverDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/applications/servers/$slug',
  component: ServerDetailPage,
  // Same disambiguator as applications — server slugs are only unique within an org.
  validateSearch: (raw): { org?: string } => {
    const o = raw['org'];
    return typeof o === 'string' ? { org: o } : {};
  },
});

const scriptDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/applications/scripts/$id',
  component: ScriptDetailPage,
  validateSearch: (raw): { org?: string } => {
    const o = raw['org'];
    return typeof o === 'string' ? { org: o } : {};
  },
});

const accessMethodsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/applications/access-methods',
  component: AccessMethodsPage,
});

const accessMethodDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/applications/access-methods/$id',
  component: AccessMethodDetailPage,
  validateSearch: (raw): { org?: string } => {
    const o = raw['org'];
    return typeof o === 'string' ? { org: o } : {};
  },
});

const secretsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/applications/secrets',
  component: SecretsPage,
});

const secretDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/applications/secrets/$id',
  component: SecretDetailPage,
  validateSearch: (raw): { org?: string } => {
    const o = raw['org'];
    return typeof o === 'string' ? { org: o } : {};
  },
});

/**
 * Legacy /p/hosting/* → /p/applications/* redirect.
 *
 * The plugin was renamed from "hosting" to "applications" (#17). Any browser
 * bookmarks or external links pointing at the old path should still land
 * somewhere useful — this catch-all reads the rest of the path and forwards.
 *
 * Lives as a sibling of the catch-all `pluginRoute` so it matches BEFORE the
 * generic /p/$pluginId branch (TanStack Router prefers static segments over
 * params).
 */
const legacyHostingRedirectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/hosting/$',
  component: LegacyHostingRedirect,
});

/** Catch-all for other plugins' pages — `/p/<plugin-id>/<sub-path>`. */
const pluginRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/p/$pluginId/$',
  component: PluginPage,
});

/**
 * Tiny component for `/p/hosting/*` → `/p/applications/*` permanent forward.
 * Runs once on mount to push the new URL with `replace: true` so the old
 * URL doesn't sit in browser history.
 */
function LegacyHostingRedirect(): JSX.Element {
  const navigate = useNavigate();
  // The catch-all $ param holds whatever followed /p/hosting/. Forward it
  // verbatim to /p/applications/<same>, preserving deep paths from old
  // bookmarks (e.g. /p/hosting/servers/web-01 → /p/applications/servers/web-01).
  const params = useParams({ strict: false }) as { _splat?: string };
  useEffect(() => {
    const rest = params._splat ?? '';
    const target = rest ? `/p/applications/${rest}` : '/p/applications';
    void navigate({ to: target, replace: true });
  }, [navigate, params._splat]);
  return <></>;
}

export const routeTree = rootRoute.addChildren([
  loginRoute,
  inviteRoute,
  appRoute.addChildren([
    dashboardRoute,
    orgsRoute,
    orgDetailRoute,
    accountRoute,
    apiKeysRoute,
    styleGuideRoute,
    applicationsIndexRoute,
    legacyHostingRedirectRoute,
    applicationDetailRoute,
    serversIndexRoute,
    serverDetailRoute,
    scriptDetailRoute,
    accessMethodsIndexRoute,
    accessMethodDetailRoute,
    secretsIndexRoute,
    secretDetailRoute,
    pluginRoute,
  ]),
]);
